import { resolveBaseUrl, resolveModerationBaseUrl } from './apiBootstrap';
import { getAuthToken, unauthorised } from './authToken';
import { readErrorDetail } from './httpError';
import type { RangeEntry } from './proposalValidation';
import type {
  PeriReviewQueue, PeriReviewOutput, PeriReviewInputs, PeriReviewRubric,
  VerdictRecord, Scores, PeriReviewMode,
} from './periReview';

export type ReviewStatus = 'signed' | 'pending' | 'stale' | 'changes_requested';
export type VerifyResp = { user_ref: string; role: string; name: string; email: string };
export type SignOffRecord = {
  registry: string; version_hash: string; registry_version: number;
  reviewer: string; decision: string; notes: string | null; timestamp: string;
};
export type ReviewRow = {
  registry: string; label: string; version: number;
  status: ReviewStatus; latest: SignOffRecord | null;
};
// Read-only companion-safety visibility (the sign-off target is retired — MA
// advises, does not sign). version/status of the registry, the loop's live-gate,
// and the provisional MA_REQUIRED gaps the MA can see but not sign.
export type CompanionSafetyVisibility = {
  registry: string;
  label: string;
  loaded: boolean;
  error: string;
  version: number | null;
  status: string | null;
  live_gate: { detection_calibration_passed: boolean };
  provisional_gaps: { authored: boolean; unauthored_paths: string[]; error?: string };
};
export type EditableCell = {
  path: string;
  section: string;
  kind: 'number' | 'enum' | 'text';
  value: unknown;
  options?: string[];
  constraint?: { min?: number; max?: number; step?: number };
  label: string;
  locked: boolean;
  lock_reason: string | null;
  required: boolean;
  overridden?: boolean;
};
export type RegistryDetail = {
  registry: string; label?: string; subset_keys?: string[];
  content: unknown; version: number;
  version_hash: string; status: ReviewStatus;
  // What still needs authoring before this can be signed. authored:false means an
  // Approve is refused server-side (422); unauthored_paths names the cells (e.g.
  // MA_REQUIRED copy, an unauthored crisis_term_list). Absent ⇒ trivially authored.
  authored?: { authored: boolean; unauthored_paths: string[] };
  // Present on MA-editable registries (editable:false otherwise).
  editable?: boolean;
  editable_cells?: EditableCell[];
  unsigned_required?: string[];
  fully_authored?: boolean;
};
export type EditItem = { path: string; value: unknown };
export type EditSignResult = {
  decision: string; version: number; content_hash: string; status: ReviewStatus;
  edit_count?: number; submission_id?: string; record?: SignOffRecord;
};
export type RevertResult = {
  reverted_submission: string; version: number; content_hash: string;
  status: ReviewStatus; edit_count: number;
};
export type SignOffResult = { record: SignOffRecord; status: ReviewStatus };
export type AuditEntry = {
  _source?: string; timestamp?: string; registry?: string;
  reviewer?: string; decision?: string; action?: string; [k: string]: unknown;
};

// --- Value proposals (bucket-B "supply the numbers" channel) ---------------
export type ProposalFieldDef = {
  key: string; label: string; type: 'number' | 'enum' | 'text' | 'select' | 'combobox';
  required: boolean; options?: string[]; default?: unknown;
};
export type ProposalFieldSchema = { kind: string; min_entries?: number; entry_fields: ProposalFieldDef[] };
export type ProposalTargetModel = {
  target: string; item_id: string; item_label: string;
  scope: { biomarker: string; jurisdiction: string };
  biomarker_known: boolean;
  current: RangeEntry[];
  field_schema: ProposalFieldSchema;
};
export type ProposalStatus = 'requested' | 'integrated' | 'declined';
export type ProposalResolution = {
  outcome: ProposalStatus; integrator: string; note: string | null;
  resulting_hash: string | null; timestamp: string;
};
export type Proposal = {
  id: string; target: string; item_id: string; item_label: string;
  scope: Record<string, unknown>;
  current_value: unknown; proposed_value: unknown;
  reviewer: string; note: string | null; timestamp: string;
  status: ProposalStatus; resolution: ProposalResolution | null;
};

async function request<T>(path: string, init: RequestInit): Promise<T> {
  const base = resolveBaseUrl();
  const res = await fetch(`${base}${path}`, init);
  if (res.status === 401) throw unauthorised(path, init);
  if (!res.ok) throw new Error(`${init.method ?? 'GET'} ${path} failed: ${res.status}`);
  return (await res.json()) as T;
}

/** Carries the HTTP status + server detail so the edit-and-sign UI can branch:
 *  409 → the registry moved (reload); 422 → an edit was rejected (show detail). */
export class ConsoleApiError extends Error {
  constructor(readonly status: number, readonly detail: string) {
    super(detail || `request failed: ${status}`);
    this.name = 'ConsoleApiError';
  }
}

async function requestTyped<T>(path: string, init: RequestInit): Promise<T> {
  const base = resolveBaseUrl();
  const res = await fetch(`${base}${path}`, init);
  if (res.status === 401) throw unauthorised(path, init);
  if (!res.ok) {
    const detail = await readErrorDetail(res, `${init.method ?? 'GET'} ${path} failed: ${res.status}`);
    throw new ConsoleApiError(res.status, detail);
  }
  return (await res.json()) as T;
}

function authedJson(): HeadersInit {
  const token = getAuthToken();
  return { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}

async function requestAt<T>(base: string, path: string, init: RequestInit): Promise<T> {
  const res = await fetch(`${base}${path}`, init);
  if (res.status === 401) throw unauthorised(path, init);
  if (!res.ok) throw new ConsoleApiError(res.status, `${init.method ?? 'GET'} ${path} failed: ${res.status}`);
  return (await res.json()) as T;
}

export type ModerationQueueItem = {
  document_id: string; has_content?: boolean; content_length?: number; [k: string]: unknown;
};
export type ModerationDocument = {
  document_id: string; content: string; content_length: number; metadata: Record<string, unknown>;
};
export type ApproveBody = {
  domain_tag: string; source_type: string; document_date: string;
  title: string; source: string; checklist_completed: boolean;
};
export type IngestedDoc = {
  document_id: string; title: string; source: string; authors: string[];
  pubmed_id: string; doi: string; domain_tag: string; source_type: string;
  evidence_tier: number | null; document_date: string | null; ingestion_timestamp: string | null;
};
export type IngestedResponse = { documents: IngestedDoc[]; total: number; offset: number; limit: number };
export type ModerationStats = {
  stats: Record<string, { chunks: number; documents: number }>;
  total_chunks?: number; total_documents?: number; error?: string;
};
export type IngestedFilters = {
  query?: string; domain?: string; source_type?: string; source?: string;
  evidence_tier?: number; limit?: number; offset?: number;
};
export type BudgetStatus = {
  status?: string; message?: string;
  total_spend_usd?: number; pilot_complete?: boolean; run_halted?: boolean;
  run_started?: string; spend_by_domain?: Record<string, number>;
  [k: string]: unknown;
};

function authed(): RequestInit {
  const token = getAuthToken();
  return { headers: token ? { Authorization: `Bearer ${token}` } : {} };
}

/** A Cognito sign-in that worked but no account carries yet (core's 403 with
 *  X-Bioweave-Auth: unlinked). Told apart by the header, never by the detail text: a
 *  403 also means cross-user access. */
export class UnlinkedSignInError extends Error {
  constructor() {
    super('this sign-in is not linked to an account');
    this.name = 'UnlinkedSignInError';
  }
}

export const consoleApi = {
  async authVerify(token: string): Promise<VerifyResp> {
    return request<VerifyResp>('/auth/verify', { headers: { Authorization: `Bearer ${token}` } });
  },
  /** /auth/verify for a Cognito access token: the same door, with the unlinked answer
   *  kept apart from a refusal. Core exposes the header through CORS. */
  async authVerifyAccess(accessToken: string): Promise<VerifyResp> {
    const init = { headers: { Authorization: `Bearer ${accessToken}` } };
    const res = await fetch(`${resolveBaseUrl()}/auth/verify`, init);
    if (res.status === 403 && res.headers.get('X-Bioweave-Auth') === 'unlinked') throw new UnlinkedSignInError();
    if (res.status === 401 || res.status === 403) throw unauthorised('/auth/verify', init, res.status);
    if (!res.ok) throw new Error(`GET /auth/verify failed: ${res.status}`);
    return (await res.json()) as VerifyResp;
  },
  async reviewQueue(): Promise<ReviewRow[]> {
    const body = await request<{ queue: ReviewRow[] }>('/advisor/review-queue', authed());
    return body.queue;
  },
  /** Read-only companion-safety visibility (no sign-off — the target is retired). */
  async companionSafetyVisibility(): Promise<CompanionSafetyVisibility> {
    return request<CompanionSafetyVisibility>('/advisor/companion-safety-visibility', authed());
  },
  async signOffHistory(registryId: string): Promise<SignOffRecord[]> {
    const body = await request<{ registry: string; history: SignOffRecord[] }>(
      `/advisor/sign-off/${encodeURIComponent(registryId)}`, authed());
    return body.history;
  },
  async getRegistry(registryId: string): Promise<RegistryDetail> {
    return request<RegistryDetail>(
      `/advisor/registries/${encodeURIComponent(registryId)}`, authed());
  },
  /** The self-contained review HTML for the current registry content (for the
   *  "Download review doc" button). Returns raw HTML text, not JSON. */
  async reviewDoc(registryId: string): Promise<string> {
    const init = authed();
    const res = await fetch(
      `${resolveBaseUrl()}/advisor/registries/${encodeURIComponent(registryId)}/review-doc`, init);
    if (res.status === 401) throw unauthorised('review-doc', init);
    if (!res.ok) throw new Error(`review-doc failed: ${res.status}`);
    return res.text();
  },
  async signOff(
    registryId: string,
    body: { decision: 'approved' | 'changes_requested'; notes: string | null; reviewed_hash?: string },
  ): Promise<SignOffResult> {
    return request<SignOffResult>(
      `/advisor/registries/${encodeURIComponent(registryId)}/sign-off`,
      { method: 'POST', headers: authedJson(), body: JSON.stringify(body) },
    );
  },
  async editSign(
    registryId: string,
    body: {
      attested_signatory: string; reviewed_hash: string;
      decision: 'approve' | 'request_changes'; edits: EditItem[]; note: string | null;
    },
  ): Promise<EditSignResult> {
    return requestTyped<EditSignResult>(
      `/advisor/registries/${encodeURIComponent(registryId)}/edit-sign`,
      { method: 'POST', headers: authedJson(), body: JSON.stringify(body) },
    );
  },
  async structureComment(registryId: string, message: string): Promise<{ routed: boolean }> {
    return requestTyped<{ routed: boolean }>(
      `/advisor/registries/${encodeURIComponent(registryId)}/comment`,
      { method: 'POST', headers: authedJson(), body: JSON.stringify({ message }) },
    );
  },
  async revert(
    registryId: string, body: { attested_signatory: string; reviewed_hash: string },
  ): Promise<RevertResult> {
    return requestTyped<RevertResult>(
      `/advisor/registries/${encodeURIComponent(registryId)}/revert`,
      { method: 'POST', headers: authedJson(), body: JSON.stringify(body) },
    );
  },
  async audit(f: { registry?: string; reviewer?: string; since?: string }): Promise<AuditEntry[]> {
    const { buildAuditQuery } = await import('./auditQuery');
    const body = await request<{ entries: AuditEntry[] }>(`/advisor/audit${buildAuditQuery(f)}`, authed());
    return body.entries;
  },
  // --- Perimenopause MA closed-cohort review -----------------------------
  periReview: {
    /** The pre-registered (MA-signed) scoring rubric. Endpoint wraps it as
     *  { rubric, status }. */
    async rubric(): Promise<PeriReviewRubric> {
      const body = await request<{ rubric: PeriReviewRubric; status?: string }>(
        '/advisor/peri-review-rubric', authed());
      return body.rubric;
    },
    /** Cohort roster + per-participant review status + headline. */
    async queue(): Promise<PeriReviewQueue> {
      return request<PeriReviewQueue>('/advisor/peri-review-queue', authed());
    },
    /** The board OUTPUT for a participant+session. Reveal-gated server-side:
     *  403 for a blinded participant until the phase-1 impression is recorded. */
    async output(participantRef: string, sessionId: string): Promise<PeriReviewOutput> {
      return request<PeriReviewOutput>(
        `/advisor/peri-review/${encodeURIComponent(participantRef)}/output?session_id=${encodeURIComponent(sessionId)}`,
        authed());
    },
    /** The as-of-run-time raw INPUTS bundle (PC-owned reconstruction endpoint),
     *  for the blinded phase-1 independent read. Always allowed. */
    async inputs(participantRef: string, sessionId: string): Promise<PeriReviewInputs> {
      return request<PeriReviewInputs>(
        `/advisor/peri-review/${encodeURIComponent(participantRef)}/inputs?session_id=${encodeURIComponent(sessionId)}`,
        authed());
    },
    /** Blinded phase-1: record the advisor's independent impression (unlocks
     *  reveal). Wire field is attested_signatory; the reviewer is resolved from
     *  the token. Response is wrapped as { record }. */
    async submitImpression(
      participantRef: string,
      body: { session_id: string; impression: string; signatory: string },
    ): Promise<{ kind: 'impression'; participant_ref: string; session_id: string; timestamp: string }> {
      const { signatory, ...rest } = body;
      const resp = await request<{ record: { kind: 'impression'; participant_ref: string; session_id: string; timestamp: string } }>(
        `/advisor/peri-review/${encodeURIComponent(participantRef)}`,
        { method: 'POST', headers: authedJson(), body: JSON.stringify({ kind: 'impression', attested_signatory: signatory, ...rest }) });
      return resp.record;
    },
    /** Record a rubric scoring (phase-2 for blinded, only phase for open). */
    async submitVerdict(
      participantRef: string,
      body: { session_id: string; review_mode: PeriReviewMode; scores: Scores; signatory: string },
    ): Promise<VerdictRecord> {
      const { signatory, ...rest } = body;
      const resp = await request<{ record: VerdictRecord }>(
        `/advisor/peri-review/${encodeURIComponent(participantRef)}`,
        { method: 'POST', headers: authedJson(), body: JSON.stringify({ kind: 'verdict', attested_signatory: signatory, ...rest }) });
      return resp.record;
    },
  },
  moderation: {
    async queue(): Promise<ModerationQueueItem[]> {
      const body = await requestAt<{ queue: ModerationQueueItem[]; total: number }>(
        resolveModerationBaseUrl(), '/queue', authed());
      return body.queue;
    },
    async document(id: string): Promise<ModerationDocument> {
      return requestAt<ModerationDocument>(
        resolveModerationBaseUrl(), `/document/${encodeURIComponent(id)}`, authed());
    },
    async approve(id: string, body: ApproveBody): Promise<{ status: string; document_id: string }> {
      return requestAt(resolveModerationBaseUrl(), `/document/${encodeURIComponent(id)}/approve`, {
        method: 'POST', headers: authedJson(), body: JSON.stringify(body),
      });
    },
    async reject(id: string, body: { reason: string }): Promise<{ status: string; document_id: string }> {
      return requestAt(resolveModerationBaseUrl(), `/document/${encodeURIComponent(id)}/reject`, {
        method: 'POST', headers: authedJson(), body: JSON.stringify(body),
      });
    },
    async stats(): Promise<ModerationStats> {
      return requestAt<ModerationStats>(resolveModerationBaseUrl(), '/stats', authed());
    },
    async ingested(f: IngestedFilters = {}): Promise<IngestedResponse> {
      const qs = new URLSearchParams();
      if (f.query) qs.set('query', f.query);
      if (f.domain) qs.set('domain', f.domain);
      if (f.source_type) qs.set('source_type', f.source_type);
      if (f.source) qs.set('source', f.source);
      if (typeof f.evidence_tier === 'number') qs.set('evidence_tier', String(f.evidence_tier));
      qs.set('limit', String(f.limit ?? 50));
      qs.set('offset', String(f.offset ?? 0));
      return requestAt<IngestedResponse>(resolveModerationBaseUrl(), `/ingested?${qs.toString()}`, authed());
    },
    async deleteIngested(id: string): Promise<{ deleted: boolean; document_id: string; chunks_deleted: number }> {
      const token = getAuthToken();
      return requestAt(resolveModerationBaseUrl(), `/ingested/${encodeURIComponent(id)}`, {
        method: 'DELETE',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
    },
    async budgetStatus(): Promise<BudgetStatus> {
      return requestAt<BudgetStatus>(resolveModerationBaseUrl(), '/budget-status', authed());
    },
    async autoReviewBatch(): Promise<Record<string, unknown>> {
      const token = getAuthToken();
      return requestAt(resolveModerationBaseUrl(), '/auto-review/batch', {
        method: 'POST', headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
    },
    async requeueRejects(): Promise<Record<string, unknown>> {
      const token = getAuthToken();
      return requestAt(resolveModerationBaseUrl(), '/auto-review/requeue-rejects', {
        method: 'POST', headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
    },
  },
  proposals: {
    /** The locked current value(s) + field schema for a scoped proposable target. */
    async target(targetId: string, itemId: string, jurisdiction = 'AU'): Promise<ProposalTargetModel> {
      const qs = new URLSearchParams({ item_id: itemId, jurisdiction });
      return request<ProposalTargetModel>(
        `/advisor/proposals/targets/${encodeURIComponent(targetId)}?${qs.toString()}`, authed());
    },
    /** Submit a proposed value + request its integration (advisor). 400/404 carry a detail. */
    async submit(body: {
      target: string; item_id: string; item_label?: string;
      scope?: Record<string, unknown>; proposed_value: unknown; note?: string | null;
    }): Promise<{ item: Proposal }> {
      return requestTyped<{ item: Proposal }>(
        '/advisor/proposals', { method: 'POST', headers: authedJson(), body: JSON.stringify(body) });
    },
    /** List proposals, newest first, optionally filtered by status / target. */
    async list(f: { status?: ProposalStatus; target?: string } = {}): Promise<Proposal[]> {
      const qs = new URLSearchParams();
      if (f.status) qs.set('status', f.status);
      if (f.target) qs.set('target', f.target);
      const q = qs.toString();
      const body = await request<{ items: Proposal[] }>(`/advisor/proposals${q ? `?${q}` : ''}`, authed());
      return body.items;
    },
    /** Mark a proposal integrated (admin — we've written + verified the value). */
    async integrate(id: string, body: { note?: string | null; resulting_hash?: string | null } = {}): Promise<{ item: Proposal }> {
      return requestTyped<{ item: Proposal }>(
        `/advisor/proposals/${encodeURIComponent(id)}/integrate`,
        { method: 'POST', headers: authedJson(), body: JSON.stringify(body) });
    },
    /** Decline a proposal (admin — a reason is required). */
    async decline(id: string, body: { reason: string }): Promise<{ item: Proposal }> {
      return requestTyped<{ item: Proposal }>(
        `/advisor/proposals/${encodeURIComponent(id)}/decline`,
        { method: 'POST', headers: authedJson(), body: JSON.stringify(body) });
    },
  },
};
